import { useState, useEffect, useRef } from 'react'
import { useVisualAutoSave } from '@/hooks/useVisualAutoSave'
import { useInterviewStore } from '@/store/interviewStore'
import { 
  Palette, Pencil, Square, Circle, Triangle, Type, 
  Eraser, RotateCcw, Download, Minus, Plus,
  Move, MousePointer, PenTool, Highlighter
} from 'lucide-react'

const DRAWING_TOOLS = [
  { id: 'select', name: 'Select', icon: MousePointer },
  { id: 'pen', name: 'Pen', icon: Pencil },
  { id: 'highlighter', name: 'Highlighter', icon: Highlighter },
  { id: 'text', name: 'Text', icon: Type },
  { id: 'rectangle', name: 'Rectangle', icon: Square },
  { id: 'circle', name: 'Circle', icon: Circle },
  { id: 'line', name: 'Line', icon: Minus },
  { id: 'eraser', name: 'Eraser', icon: Eraser }
]

const COLORS = [
  '#000000', '#FF0000', '#00FF00', '#0000FF', 
  '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500',
  '#800080', '#008000', '#000080', '#808080'
]

const STROKE_WIDTHS = [1, 2, 4, 8, 12]

export default function WhiteboardDiscussion() {
  const store = useInterviewStore()
  const whiteboardData = store.getWhiteboardData()
  const { isDirty, lastSaved } = useVisualAutoSave({ 
    stage: 'whiteboard',
    intervalMs: 5000 
  })

  // Drawing state
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)

  // Always keep the discussion in drawing mode
  useEffect(() => {
    if (whiteboardData.drawingMode !== 'draw') {
      store.setWhiteboardMode('draw')
    }
  }, [store, whiteboardData.drawingMode])

  const getSaveStatus = () => {
    if (isDirty) {
      return <span className="text-amber-600">● Unsaved changes</span>
    } else if (lastSaved) {
      return <span className="text-green-600">✓ Saved {new Date(lastSaved).toLocaleTimeString()}</span>
    }
    return <span className="text-gray-500">No changes</span>
  }

  // Canvas drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (whiteboardData.selectedTool === 'select' || whiteboardData.selectedTool === 'text') return
    
    setIsDrawing(true)
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    ctx.beginPath()
    ctx.moveTo(x, y)
    
    // Set drawing properties
    ctx.strokeStyle = whiteboardData.selectedColor
    ctx.lineWidth = whiteboardData.strokeWidth
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    
    if (whiteboardData.selectedTool === 'highlighter') {
      ctx.globalAlpha = 0.3
    } else if (whiteboardData.selectedTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out'
    } else {
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    }
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || whiteboardData.selectedTool === 'select' || whiteboardData.selectedTool === 'text') return
    
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (whiteboardData.selectedTool === 'pen' || whiteboardData.selectedTool === 'highlighter' || whiteboardData.selectedTool === 'eraser') {
      ctx.lineTo(x, y)
      ctx.stroke()
    }
  }

  const stopDrawing = () => {
    setIsDrawing(false)
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.beginPath()
    
    // Save canvas state to store with optimization
    try {
      const imageData = canvas.toDataURL('image/jpeg', 0.8) // Use JPEG with compression for smaller size
      
      // Check size and warn if too large
      if (imageData.length > 2000000) {
        console.warn(`⚠️ Canvas data is large (${imageData.length} chars). Consider clearing for better performance.`)
      }
      
      store.updateWhiteboardCanvas(imageData)
    } catch (error) {
      console.warn('⚠️ Failed to save canvas data:', error)
    }
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Reset canvas background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Clear canvas data in store
    store.updateWhiteboardCanvas('')
  }

  const downloadCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const link = document.createElement('a')
    link.download = 'whiteboard-sketch.png'
    link.href = canvas.toDataURL()
    link.click()
  }

  // Initialize canvas and restore saved canvas data
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    canvas.width = canvas.offsetWidth
    canvas.height = canvas.offsetHeight

    if (!canvas.width || canvas.width < 10) {
      canvas.width = 960
    }
    if (!canvas.height || canvas.height < 10) {
      canvas.height = 540
    }

    // Set white background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Restore saved canvas data if available
    if (whiteboardData.canvasData) {
      try {
        // Validate that the canvas data is a proper data URL
        if (whiteboardData.canvasData.startsWith('data:image/')) {
          // Check if the data URL is too long (some browsers have limits)
          const dataUrlLength = whiteboardData.canvasData.length
          if (dataUrlLength > 2000000) { // 2MB limit
            console.warn(`⚠️ Canvas data URL is very large (${dataUrlLength} chars), may cause browser issues`)
          }
          
          const image = new Image()
          image.onload = () => {
            try {
              ctx.drawImage(image, 0, 0)
              console.log(`✅ Canvas data restored successfully (${dataUrlLength} chars)`)
            } catch (error) {
              console.warn('⚠️ Failed to draw image to canvas:', error)
            }
          }
          image.onerror = (error) => {
            console.warn('⚠️ Failed to load canvas image data. This may be due to browser data URL size limits:', error)
            console.warn(`Canvas data length: ${dataUrlLength} characters`)
          }
          image.src = whiteboardData.canvasData
        } else {
          console.warn('⚠️ Invalid canvas data format:', whiteboardData.canvasData.substring(0, 50))
        }
      } catch (error) {
        console.warn('⚠️ Error restoring canvas data:', error)
      }
    }
  }, [whiteboardData.canvasData])

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Palette className="w-6 h-6 text-orange-600" />
            <h2 className="text-lg font-semibold">Whiteboard Discussion</h2>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-sm">
              {getSaveStatus()}
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={clearCanvas}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Clear</span>
              </button>
              <button
                onClick={downloadCanvas}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          Use drawing tools to sketch ideas and diagrams.
        </div>
      </div>

      {/* Drawing Tools */}
      <div className="border-b border-gray-200 p-3">
        <div className="flex items-center space-x-4">
          {/* Tools */}
          <div className="flex space-x-1">
            {DRAWING_TOOLS.map((tool) => {
              const Icon = tool.icon
              return (
                <button
                  key={tool.id}
                  onClick={() => store.setWhiteboardTool(tool.id)}
                  className={`p-2 rounded-md transition-colors ${
                    whiteboardData.selectedTool === tool.id
                      ? 'bg-orange-100 text-orange-700 border-2 border-orange-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                  title={tool.name}
                >
                  <Icon className="w-4 h-4" />
                </button>
              )
            })}
          </div>

          {/* Colors */}
          <div className="flex space-x-1">
            {COLORS.map((color) => (
              <button
                key={color}
                onClick={() => store.setWhiteboardColor(color)}
                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                  whiteboardData.selectedColor === color ? 'border-gray-800' : 'border-gray-300'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>

          {/* Stroke Width */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Size:</span>
            <div className="flex space-x-1">
              {STROKE_WIDTHS.map((width) => (
                <button
                  key={width}
                  onClick={() => store.setWhiteboardStrokeWidth(width)}
                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                    whiteboardData.strokeWidth === width
                      ? 'bg-orange-100 text-orange-700 border-2 border-orange-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                >
                  <div 
                    className="bg-current rounded-full"
                    style={{ width: `${width + 2}px`, height: `${width + 2}px` }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Drawing Canvas */}
      <div className="flex-1 p-4">
        <div className="h-full">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Drawing Canvas</h3>
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="w-full h-full border-2 border-gray-300 rounded-lg bg-white cursor-crosshair"
            style={{ minHeight: '500px' }}
          />
        </div>
      </div>
    </div>
  )
}
